'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getMyListings } from '@/lib/api/listings';
import { useI18n, getLocalizedField } from '@/i18n';

/** 状态标签 */
function buildStatusBadge(t) {
    return {
        0: { label: t('promote.statusBadge.draft'), cls: 'lp-status--draft' },
        1: { label: t('promote.statusBadge.pending'), cls: 'lp-status--pending' },
        2: { label: t('promote.statusBadge.active'), cls: 'lp-status--active' },
        3: { label: t('promote.statusBadge.sold'), cls: 'lp-status--sold' },
        4: { label: t('promote.statusBadge.off'), cls: 'lp-status--off' },
        5: { label: t('promote.statusBadge.expired'), cls: 'lp-status--expired' },
        6: { label: t('promote.statusBadge.reject'), cls: 'lp-status--reject' },
    };
}

/** 价格格式化 */
function formatPrice(item, t) {
    if (item.priceUsd && Number(item.priceUsd) > 0) return `$${item.priceUsd}`;
    if (item.priceGnf && Number(item.priceGnf) > 0) return `${item.priceGnf} GNF`;
    return t('promote.picker.priceTBD');
}

/** 提取列表：兼容多种返回结构 */
function extractList(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw.data)) return raw.data;
    if (raw.data && Array.isArray(raw.data.records)) return raw.data.records;
    if (Array.isArray(raw.records)) return raw.records;
    return [];
}

/** 提取总数 */
function extractTotal(raw) {
    if (!raw) return 0;
    if (typeof raw.total === 'number') return raw.total;
    if (raw.data && typeof raw.data.total === 'number') return raw.data.total;
    return extractList(raw).length;
}

/** 占位符替换 */
function fmt(str, params) {
    return Object.entries(params || {}).reduce((s, [k, v]) => s.replace(`{${k}}`, v), str);
}

export default function ListingPickerModal({ open, onClose, plan, onConfirm }) {
    const { t, lang } = useI18n();
    const [keyword, setKeyword] = useState('');
    const [debounced, setDebounced] = useState('');
    const [pageNum, setPageNum] = useState(1);
    const pageSize = 10;
    const [selectedId, setSelectedId] = useState(null);
    const inputRef = useRef(null);
    const scrollRef = useRef(null);
    const STATUS_BADGE = buildStatusBadge(t);

    // 关键词防抖
    useEffect(() => {
        const tm = setTimeout(() => {
            setDebounced(keyword.trim());
            setPageNum(1);
        }, 350);
        return () => clearTimeout(tm);
    }, [keyword]);

    // 弹窗打开时 focus 搜索框
    useEffect(() => {
        if (open) {
            setKeyword('');
            setDebounced('');
            setPageNum(1);
            setSelectedId(null);
            setTimeout(() => inputRef.current?.focus(), 80);
        }
    }, [open]);

    const { data, isLoading, isFetching, error } = useQuery({
        queryKey: ['listing', 'mine', { keyword: debounced, pageNum, pageSize }],
        queryFn: () =>
            getMyListings({ keyword: debounced, pageNum, pageSize }).then((res) => ({
                list: extractList(res),
                total: extractTotal(res),
            })),
        enabled: open,
        keepPreviousData: true,
        staleTime: 30 * 1000,
    });

    const list = data?.list ?? [];
    const total = data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    // ESC 关闭
    const handleKeyDown = useCallback(
        (e) => {
            if (e.key === 'Escape') onClose?.();
        },
        [onClose]
    );
    useEffect(() => {
        if (open) document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open, handleKeyDown]);

    // 选中后滚动到可见
    useEffect(() => {
        if (selectedId && scrollRef.current) {
            const el = scrollRef.current.querySelector(`[data-lp-id="${selectedId}"]`);
            el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [selectedId]);

    if (!open) return null;

    const pt = (k) => t(`promote.picker.${k}`);

    return (
        <div className="lp-root" onClick={onClose}>
            <div className="lp-backdrop" />
            <div className="lp-dialog" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="lp-head">
                    <div>
                        <div className="lp-title">{pt('title')}</div>
                        <div className="lp-subtitle">
                            {pt('planLabel')}<span className="lp-plan-name">{getLocalizedField(plan, "planTypeName", lang) || (plan?.planTypeName ?? '')}</span> ·{' '}
                            <span className="lp-plan-price">{plan ? `$${plan.priceUsd ?? 0}` : ''}</span>
                        </div>
                    </div>
                    <button className="lp-close" onClick={onClose} aria-label={pt('close')}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* Search */}
                <div className="lp-search">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder={pt('searchPlaceholder')}
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                    />
                    {keyword && (
                        <button className="lp-search-clear" onClick={() => setKeyword('')} aria-label={pt('clear')}>
                            ✕
                        </button>
                    )}
                </div>

                {/* List */}
                <div className="lp-list-wrap" ref={scrollRef}>
                    {isLoading ? (
                        <div className="lp-empty lp-empty--loading">
                            <div className="lp-spinner" />
                            {pt('loading')}
                        </div>
                    ) : error ? (
                        <div className="lp-empty lp-empty--err">{pt('loadFail')}</div>
                    ) : list.length === 0 ? (
                        <div className="lp-empty">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                            <div>{debounced ? fmt(pt('noResults'), { kw: debounced }) : pt('noListings')}</div>
                        </div>
                    ) : (
                        <div className="lp-list">
                            {list.map((item) => {
                                const sel = selectedId === item.id;
                                const badge = STATUS_BADGE[item.status] ?? STATUS_BADGE[0];
                                return (
                                    <div
                                        key={item.id}
                                        data-lp-id={item.id}
                                        className={`lp-item ${sel ? 'lp-item--sel' : ''}`}
                                        onClick={() => setSelectedId(item.id)}
                                    >
                                        <div className="lp-thumb">
                                            {item.coverUrl ? (
                                                <img src={item.coverUrl} alt="" loading="lazy" />
                                            ) : (
                                                <div className="lp-thumb-ph">
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2" />
                                                        <circle cx="6.5" cy="16.5" r="2.5" />
                                                        <circle cx="16.5" cy="16.5" r="2.5" />
                                                    </svg>
                                                </div>
                                            )}
                                            {item.isVerified === 1 && (
                                                <span className="lp-verify" title={pt('verified')}>✓</span>
                                            )}
                                        </div>
                                        <div className="lp-item-body">
                                            <div className="lp-item-title">{item.title}</div>
                                            <div className="lp-item-meta">
                                                {item.brandName && (
                                                    <span className="lp-chip">{item.brandName}</span>
                                                )}
                                                {item.modelName && (
                                                    <span className="lp-chip lp-chip--model">{item.modelName}</span>
                                                )}
                                                {item.carYear && (
                                                    <span className="lp-chip">{item.carYear}</span>
                                                )}
                                            </div>
                                            <div className="lp-item-footer">
                                                <span className="lp-item-price">{formatPrice(item, t)}</span>
                                                <span className={`lp-status ${badge.cls}`}>{badge.label}</span>
                                            </div>
                                        </div>
                                        <div className={`lp-radio ${sel ? 'lp-radio--on' : ''}`}>
                                            {sel && (
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="20 6 9 17 4 12" />
                                                </svg>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {list.length > 0 && (
                    <div className="lp-pager">
                        <button
                            className="lp-page-btn"
                            disabled={pageNum <= 1 || isFetching}
                            onClick={() => setPageNum((p) => Math.max(1, p - 1))}
                        >
                            {pt('prevPage')}
                        </button>
                        <span className="lp-page-info">
                            {fmt(pt('pageInfo'), { page: pageNum, totalPages, total })}
                        </span>
                        <button
                            className="lp-page-btn"
                            disabled={pageNum >= totalPages || isFetching}
                            onClick={() => setPageNum((p) => Math.min(totalPages, p + 1))}
                        >
                            {pt('nextPage')}
                        </button>
                    </div>
                )}

                {/* Footer */}
                <div className="lp-foot">
                    <button className="lp-btn lp-btn--ghost" onClick={onClose}>{pt('cancel')}</button>
                    <button
                        className="lp-btn lp-btn--primary"
                        disabled={!selectedId}
                        onClick={() => {
                            const picked = list.find((i) => i.id === selectedId);
                            if (picked) onConfirm?.(picked, plan);
                        }}
                    >
                        {selectedId ? pt('confirm') : pt('pleasePick')}
                    </button>
                </div>
            </div>
        </div>
    );
}
